import { within } from "./editDistance.js";
// >>> **WHETHER A THING SOMEBODY TYPED IS AN ADDRESS, AND WHAT SHE PROBABLY MEANT.** <<<
//
// Her order, 2026-08-14, off the broadcast recovery's own finding: *real-format validation at the
// field (warm inline error, never a silent block), a common-typo suggester (gnail/gamil/yahoo
// variants offered as "did you mean"), and the existing 14 broken-address members flagged for a warm
// in-app prompt.*
//
// **THE FINDING THAT ORDERED THIS: FOURTEEN MEMBERS SIGNED UP IN EIGHT DAYS WITH AN ADDRESS THAT
// CANNOT RECEIVE MAIL** — `i@mmoney888!`, `@g.comtec`, `d@j@nique09`, `blainesfamily@gmail:com`,
// `candace_mutcherson@yahoo`, `tiffany488@.com`, `khyijien1@`, `veronicayeager00@gmail`,
// `josie@0825`, `cecila.carson@gnail` and four more. **Every one came through the EMAIL path with no
// Google sub**, and the field they were typed into is `<input value={email} required />` — **no
// `type="email"`, so the browser's own format check never ran.** They will never receive a welcome
// email, a card reminder or anything else, and nothing on this site ever told them.
//
// **IT IS A SHARED `.js` LAW BECAUSE THE SERVER TAKES THE ADDRESS TOO** (`strings.js`' own reason).
// A client-only check is a check a request can walk straight past, and a server-only one is a member
// refused with no idea why. One law, both ends.
//
// **AND `addressLooksSendable` IS THIS SAME QUESTION, ASKED BY THE MAIL RAIL, SO IT IS THIS FUNCTION**
// (R28/R29). The broadcast recovery wrote it to decide which failures could never be retried; a
// second spelling here would be two answers to *is this an address* — and the one that drifted would
// be the one deciding whether a member can be reached at all.

/**
 * >>> **IS THIS A DELIVERABLE SHAPE?** <<<
 *
 * **DELIBERATELY CONSERVATIVE, AND THE DIRECTION IS THE RULING (R19/R8-4).** A refusal is VISIBLE —
 * she is standing at the field and is told — while acceptance is silent and costs her every message
 * this house will ever send. But refusing wrongly turns somebody away at the door, so this asks only
 * what is genuinely universal: a local part, one `@`, a domain with a dot in it, and no whitespace.
 *
 * **IT DOES NOT TRY TO BE AN RFC PARSER.** The full grammar admits quoted local parts and bracketed
 * literals that no member is typing and no provider is issuing, and a validator that models them is
 * one nobody can read. Every one of the fourteen fails this, which is the calibration that matters.
 */
// >>> **AND THE CHARACTERS THAT BREAK A MAIL HEADER ARE REFUSED BY NAME, BECAUSE ONE OF THE FIFTEEN
// IS EXACTLY THAT AND THE SHAPE TEST ALONE LET IT THROUGH.** <<< `"lala@aquarius.io"` — her own
// address stored WITH LITERAL QUOTE CHARACTERS — passes every structural test there is and Gmail
// answered it **`Invalid To header`, twice.** These are the characters the RFC gives a special
// meaning inside an address and that no provider issues in one, so refusing them costs nobody a
// signup and closes the one case a shape test cannot see.
// **THE APOSTROPHE IS DELIBERATELY NOT ON THIS LIST, AND IT WAS TAKEN OFF AFTER MEASURING.** It is
// legal in an unquoted local part, providers really issue it, and `o'brien@aol.com` is a person —
// so refusing it turns a member away at the door, which is the one direction a signup gate may not
// fail in (R19). It breaks no header either; the double quote and the separators do.
const HEADER_BREAKERS = /["‚,;<>()[\]\\]/;
export const emailShapeValid = (email) => {
  const raw = String(email ?? "").trim();
  return !HEADER_BREAKERS.test(raw) && /^[^@\s]+@[^@\s.]+(?:\.[^@\s.]+)+$/.test(raw);
};

/** The one sentence the house says about a shape it cannot send to. Warm, and it names the fix. */
export const EMAIL_SHAPE_HINT = "That does not look like an email address yet. Check for a missing @ or a missing .com.";

/**
 * The providers this house's members really use, measured off the live table rather than guessed:
 * gmail, yahoo, icloud, hotmail, outlook, aol, proton and comcast cover the overwhelming majority.
 * **The roster exists so a SUGGESTION can be offered — it is never a whitelist**, and an address at
 * a domain nobody here has heard of is perfectly valid and is left alone (R19: over-refusing at a
 * signup door costs a member).
 */
// **AND THE ROSTER'S OWN EARLY RETURN IS WHAT PROTECTS A REAL DOMAIN FROM A WRONG SUGGESTION**, so
// the second half of this list is here for exactly that: `email.com`, `aim.com`, `ymail.com` and
// `gmx.com` are REAL providers that sit one or two edits from a bigger one. **`email.com` was
// measured being told *did you mean gmail.com* before it was added** — one substitution away — which
// is a wrong did-you-mean shown to a real member, found by this round's own mutation battery.
export const COMMON_MAIL_DOMAINS = [
  "gmail.com", "yahoo.com", "icloud.com", "hotmail.com", "outlook.com",
  "aol.com", "protonmail.com", "proton.me", "comcast.net", "live.com", "msn.com", "me.com",
  "email.com", "aim.com", "ymail.com", "gmx.com", "mail.com", "rocketmail.com", "att.net", "verizon.net",
];

/**
 * >>> **DAMERAU, NOT PLAIN LEVENSHTEIN, AND HER OWN EXAMPLE IS WHY.** <<< She named `gamil`, and
 * `gamil` -> `gmail` is a **transposition of two adjacent letters** — which plain Levenshtein scores
 * as TWO edits, so a distance-1 suggester answers nothing on the exact typo her order names. The
 * same is true of `hotmial` and `yhaoo`. **A transposition is one slip of two fingers and is the
 * commonest typo there is**, so it counts as one edit; widening the threshold to 2 instead would
 * start suggesting `gmail.com` to somebody at a real domain two letters away.
 */
// >>> LIFTED to `src/editDistance.js` (2026-08-25), because her misspelled-seal item gave it a
// SECOND caller. The reasoning above is unchanged and still governs; only the BODY moved.


/**
 * >>> **WHAT SHE PROBABLY MEANT — OFFERED, NEVER APPLIED.** <<<
 *
 * Her clause names `gnail` and `gamil`. **AND THE MEASURED CASES INCLUDE A SECOND SHAPE SHE DID NOT
 * NAME AND WHICH THREE OF THE FOURTEEN ARE**: a domain with NO DOT AT ALL —
 * `candace_mutcherson@yahoo`, `veronicayeager00@gmail`, `cecila.carson@gnail`. Both are handled, and
 * the second one is why the stem is compared before the dot test rather than after it.
 *
 * **IT SUGGESTS AND DOES NOT CORRECT, WHICH IS THE WHOLE DESIGN.** Silently rewriting somebody's
 * address is this round's own defect wearing a fix: a member at `gnail.co.uk` — a real domain —
 * would be signed up as somebody else, and she would never know until nothing arrived.
 *
 * **A DOMAIN THAT IS ALREADY RIGHT SUGGESTS NOTHING** (R43's M8): a suggester that fires on
 * `gmail.com` is a prompt every member on this site is asked to dismiss.
 */
export function emailTypoSuggestion(email) {
  const raw = String(email ?? "").trim();
  const at = raw.lastIndexOf("@");
  if (at <= 0 || at === raw.length - 1) return "";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1).toLowerCase();
  if (!local || /\s/.test(local)) return "";
  if (COMMON_MAIL_DOMAINS.includes(domain)) return "";

  // A domain with no dot: `gmail` and `gnail` both resolve here, and the stem is compared against the
  // roster's own stems so `yahoo` -> `yahoo.com` needs no second table.
  if (!domain.includes(".")) {
    const hit = COMMON_MAIL_DOMAINS.find((d) => {
      const stem = d.slice(0, d.lastIndexOf("."));
      return stem === domain || within(domain, stem, 1) <= 1;
    });
    return hit ? `${local}@${hit}` : "";
  }
  // One edit away from a domain this house's members really use.
  const near = COMMON_MAIL_DOMAINS.find((d) => within(domain, d, 1) <= 1);
  return near ? `${local}@${near}` : "";
}

/**
 * >>> **THE FIELD'S OWN STATE, IN ONE LAW A GATE CAN RUN.** <<<
 *
 * **FOUR STATES, NEVER TWO (R18), AND THE FIRST ONE IS WHY.** An EMPTY field is not a wrong address —
 * a member who has not finished typing must not be told she has made a mistake, which is the fastest
 * way to make somebody abandon a signup. `typo` is a valid address that is probably not the one she
 * meant, and it is a SUGGESTION rather than a refusal: `josie@0825.com` would be a real address.
 *
 * **HER *never a silent block* CLAUSE IS THE `hint`.** A door that refuses and says nothing is the
 * thing she is forbidding; `blocking` is only ever true beside a sentence that explains it.
 */
export function emailFieldState(email) {
  const raw = String(email ?? "").trim();
  if (!raw) return { state: "empty", hint: "", suggestion: "", blocking: false };
  if (!emailShapeValid(raw)) return { state: "invalid", hint: EMAIL_SHAPE_HINT, suggestion: emailTypoSuggestion(raw), blocking: true };
  const suggestion = emailTypoSuggestion(raw);
  if (suggestion) return { state: "typo", hint: `Did you mean ${suggestion}?`, suggestion, blocking: false };
  return { state: "ok", hint: "", suggestion: "", blocking: false };
}

/**
 * >>> **HER CLAUSE 3: THE MEMBERS WHOSE ADDRESS ALREADY CANNOT RECEIVE MAIL.** <<<
 *
 * The stamp is `users.email_undeliverable_at`, written by the broadcast recovery. **The prompt is
 * warm and it does not blame her** — she typed one character wrong eight days ago and this house
 * never told her — and it names what she is MISSING rather than what she did, because that is the
 * part she can act on.
 *
 * **AND IT IS SHOWN ONLY WHERE SHE CAN DO SOMETHING ABOUT IT.** A banner on every page for a thing
 * with no door behind it is nagging; this rides the account screen, where the address lives.
 */
export const UNDELIVERABLE_PROMPT_TITLE = "We cannot reach you by email";
export const UNDELIVERABLE_PROMPT_BODY =
  "Messages we send to your address are coming back to us, so you are missing your readings, your reminders and anything Lala sends the house."
  + " Check the spelling below and save it, and we will start reaching you again.";
